import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { SetURLSearchParams } from "react-router-dom";
import type { SearchFilters } from "../../components/FilterBar/filterChipDefs";
import { paramsToFilters, filtersToParams } from "./filterParams";

type SetFiltersOptions = Parameters<SetURLSearchParams>[1];

/**
 * Returns a SearchFilters value derived from the current URL search params,
 * along with a setter that writes a new SearchFilters back to the URL.
 *
 * The URL is the single source of truth — any external writer that mutates
 * the URL (e.g. setSearchParams from another component) is reflected on the
 * next render. Holding filters in component state would let the URL drift
 * without triggering a refetch.
 */
export function useUrlSyncedFilters(): [
  SearchFilters,
  (next: SearchFilters, options?: SetFiltersOptions) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => paramsToFilters(searchParams), [searchParams]);
  const setFilters = (next: SearchFilters, options?: SetFiltersOptions) =>
    setSearchParams(filtersToParams(next), options);
  return [filters, setFilters];
}
